<template
  ><div
    draggable="false"
    @mousedown="onMouseDown"
    @touchstart="onTouchDown"
    @click="onClick"
  >
    <slot></slot></div
></template>

<script>
import { ViewportHelper } from "@/helpers/viewport-helper";

export default {
  name: "DraggableItem",
  props: {
    entry: {
      type: Object,
      required: true
    },
    draggable: {
      type: Boolean,
      required: false,
      default: false
    },
    cloneOnDrag: {
      type: Boolean,
      default: false
    }
  },
  data: () => {
    return {
      dragTimeout: 200,
      dragHoldTimeout: 500,
      minHorizontalDragDistance: 5,
      minVerticalDragDistance: 5,
      interactionId: null,
      isTouchInteraction: false,
      dragReady: false, // to prevent mixup of drag and scroll
      dragging: false, // for drag logic
      dragMode: false, // for view update
      dragStartX: -1,
      dragStartY: -1,
      pointerDeltaX: 0,
      pointerDeltaY: 0
    };
  },
  methods: {
    onMouseDown(event) {
      if (!this.draggable || this.interactionId !== null) {
        return;
      }
      this.isTouchInteraction = false;
      this.onPointerDown(event);
    },
    onTouchDown(event) {
      if (!this.draggable) {
        return;
      }
      this.isTouchInteraction = true;
      document.body.style.userSelect = "pan-x pan-y";
      document.body.style.webkitUserSelect = "none";
      this.onPointerDown(event);
    },
    onPointerDown(event) {
      event.stopPropagation();

      const interactionId = this.$uuid.v4();
      const pointerPosition = ViewportHelper.getViewportPosition(event);
      const elementPosition = ViewportHelper.getElementViewportPosition(
        this.$el
      );
      const posX = elementPosition.x;
      const posY = elementPosition.y;

      this.hookDragListeners();
      this.dragging = false;
      this.dragReady = false;
      this.interactionId = interactionId;
      this.dragStartX = pointerPosition.x;
      this.dragStartY = pointerPosition.y;
      this.pointerDeltaX = pointerPosition.x - posX;
      this.pointerDeltaY = pointerPosition.y - posY;

      let selection = window.getSelection();
      if (selection) {
        selection.empty();
      }

      if (this.isTouchInteraction) {
        setTimeout(() => {
          if (this.interactionId !== interactionId) {
            return;
          }
          this.dragReady = true;
        }, this.dragTimeout);
      } else {
        this.dragReady = true;
      }
    },
    onPointerMove(event) {
      if (this.dragging || !this.interactionId) {
        return;
      }

      const pointerPosition = ViewportHelper.getViewportPosition(event);
      const dragDeltaX = pointerPosition.x - this.dragStartX;
      const dragDeltaY = pointerPosition.y - this.dragStartY;
      const triggeredX = Math.abs(dragDeltaX) >= this.minHorizontalDragDistance;
      const triggeredY =
        Math.abs(dragDeltaY) - Math.abs(dragDeltaX) / 2 >=
        this.minVerticalDragDistance;
      if (!triggeredX && !triggeredY) {
        return;
      }

      if (this.isTouchInteraction && !this.dragReady && triggeredY) {
        this.dragMode = false;
        this.interactionId = null;
        this.releaseDragHooks();
        return;
      }
      // alert("drag start! "+this.isTouchInteraction+","+this.dragReady+","+triggeredX+","+triggeredY);
      this.onDragStart(event);
    },
    onPointerUp(event) {
      this.interactionId = null;
      this.releaseDragHooks();
      if (this.dragging) {
        this.onDragEnd(event);
      }
      document.body.style.userSelect = null;
      document.body.style.webkitUserSelect = null;
    },
    onClick(event) {
      if (this.dragging) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    // eslint-disable-next-line no-unused-vars
    onDragStart() {
      if (this.isTouchInteraction) {
        this.$emit("touchend"); // ensure touch scroll is interrupted
      }
      this.dragging = true;
      const interactionId = this.interactionId;
      setTimeout(() => {
        if (this.interactionId !== interactionId) {
          return;
        }
        this.dragMode = true;
      }, 11);

      const elementPosition = ViewportHelper.getElementViewportPosition(
        this.$el
      );
      const posX = elementPosition.x;
      const posY = elementPosition.y;
      // const dragDeltaX = event.pageX - this.dragStartX;
      // const dragDeltaY = event.pageY - this.dragStartY;
      const data = {
        uid: this.uid,
        entry: this.entry,
        posX: posX, // x position of this candidate
        posY: posY, // y position of this candidate
        deltaX: this.pointerDeltaX, // x distance between pointer position and this candidate when interaction started
        deltaY: this.pointerDeltaY, // y distance between pointer position and this candidate when interaction started
        initiationDeltaX: 0, //dragDeltaX, // x distance the cursor moved to trigger the drag
        initiationDeltaY: 0, //dragDeltaY, // y distance the cursor moved to trigger the drag
        isTouchInteraction: this.isTouchInteraction
      };
      this.$parent.$emit("itemdragstart", data);
    },
    /**
     * @param event The pointer up event that triggered the end of the drag interaction
     */
    onDragEnd(event) {
      this.dragging = false;
      this.dragMode = false;
      event.preventDefault();
      event.stopPropagation();
      this.$parent.$emit("itemdragend");
    },
    hookDragListeners() {
      if (this.isTouchInteraction) {
        document.addEventListener("touchmove", this.onPointerMove);
        document.addEventListener("touchend", this.onPointerUp);
        document.addEventListener("contextmenu", this.onContextMenu);
      } else {
        document.addEventListener("mousemove", this.onPointerMove);
        document.addEventListener("mouseup", this.onPointerUp);
      }
      document.addEventListener("selectstart", this.onSelectStart);
      document.addEventListener("selectionchange", this.onSelectStart);
      document.addEventListener("select", this.onSelect);
    },
    releaseDragHooks() {
      if (this.isTouchInteraction) {
        document.removeEventListener("touchmove", this.onPointerMove);
        document.removeEventListener("touchend", this.onPointerUp);
        document.removeEventListener("contextmenu", this.onContextMenu);
      } else {
        document.removeEventListener("mousemove", this.onPointerMove);
        document.removeEventListener("mouseup", this.onPointerUp);
      }
      document.removeEventListener("selectstart", this.onSelect);
      document.removeEventListener("selectionchange", this.onSelect);
      document.removeEventListener("select", this.onSelect);
    },
    onSelect(event) {
      event.stopPropagation();
      event.preventDefault();
    },
    onContextMenu(event) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
};
</script>
